import { ButtonBase, IconButton } from '@mui/material'
import type { ContainerTypeMap } from '@mui/system'
import { Container } from '@mui/system'
import { useAtomValue } from 'jotai'
import type { FC, PropsWithChildren } from 'react'
import { memo, useEffect, useMemo } from 'react'
import { Link } from 'react-router'
import { useMeasure } from 'react-use'
import { withQuery } from 'ufo'
import IcBaselineAdd from '~icons/ic/baseline-add'
import MingcuteSearchLine from '~icons/mingcute/search-line'
import AppFooter from 'src/components/App/AppFooter'
import { CustomHeroTitleWithDescription } from 'src/components/Common/SiteName'
import { DiscordButton } from 'src/components/Discord'
import { simpleGenFormStateAtom } from 'src/components/GenerationTask/SimpleGenerationForm.store'
import AppNavigation, {
  APP_NAVIGATION_HEIGHT,
} from 'src/components/Navbar/AppNavigation'
import NotificationButton from 'src/components/Notification/NotificationButton'
import OpenAppBanner from 'src/components/OpenInApp'
import MyCredits from 'src/components/Quota/MyCredits'
import SearchBar, { MobileSearchBar } from 'src/components/Search/SearchBar'
import Text from 'src/components/ui/Text'
import { AppTopBannerSlot } from 'src/features/Growth/growth-slots/AppTopBannerSlot'
import { useMyInfo } from 'src/services/apollo/auth'
import { headerBannerAtom, headerCreditPillAtom } from 'src/stores/banner'
import { desktopNavigationExpandAtom } from 'src/stores/navigation'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { useHideableNavbar } from 'src/utils/hooks/useHideableNavbar'
import { usePickAtomValue } from 'src/utils/jotai'
import {
  useLocalePath,
  useLocation,
  useRouteHandleFlag,
} from 'src/utils/router/route'
import { startEventGroup, track } from 'src/utils/tracker'
import { AppLogoSlot } from '~/features/Growth/growth-slots/AppLogoSlot'
import AccountButton from '../components/Account/AccountButton'
import { cx } from '../utils/cx'
import styles from './AppLayout.module.css'

/**
 * Config for controlling the visibility of the header search bar.
 *
 * `true`: Always visible
 * `"top"`: Only visible when the simple generation form is in-view.
 */
const ROUTES_TO_SHOW_SEARCH_BAR: Record<string, boolean | 'top'> = {
  '/': 'top',
  '/market': true,
  '/search': true,
}

const AppHeader: FC = memo(() => {
  const isMobile = useIsMobile()
  const isDesktop = !isMobile
  const barHeight = isMobile ? 48 : 64
  // oxfmt-ignore
  const [bannerContainer, { height: bannerContainerHeight }] = useMeasure<HTMLDivElement>()

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--app-header-height',
      barHeight + bannerContainerHeight + 'px',
    )
  }, [barHeight, bannerContainerHeight])

  const location = useLocation()
  const localePath = useLocalePath()
  const logoPath = localePath('/')
  const { scrolledOut: simpleGenFormScrolledOut } = usePickAtomValue(
    simpleGenFormStateAtom,
    ['scrolledOut'],
  )
  const searchBarConfig = ROUTES_TO_SHOW_SEARCH_BAR[location.pathname]
  const showingSearchBar =
    Boolean(searchBarConfig) &&
    (searchBarConfig === 'top' ? !simpleGenFormScrolledOut : true)

  const showCreditPill = useAtomValue(headerCreditPillAtom)

  const showGenerateButton =
    isDesktop && (location.pathname !== '/' || simpleGenFormScrolledOut)

  /**
   * Render
   */

  return (
    <>
      <div className="sticky top-0 w-full z-20">
        {/* Banner container */}
        <div ref={bannerContainer}>
          <LayoutHeaderBanner />
        </div>
        {/* The actual header */}
        <header
          className={cx(
            'flex items-center bg-background/95 backdrop-blur-md',
            isMobile ? 'px-3 gap-2' : 'px-4 gap-4',
          )}
          style={{
            height: barHeight,
          }}
        >
          {/* Logo */}
          <Link
            to={logoPath}
            className={cx(
              'group flex-none inline-flex items-center py-3 no-underline',
              isDesktop && 'px-3',
            )}
          >
            <div className="size-7 mmd:size-9">
              <AppLogoSlot />
            </div>
            <CustomHeroTitleWithDescription
              title={<Text i18nKey="common:artwork-list.hero.title" />}
              size="small"
              className="ml-2 !mt-0 max-mmd:hidden"
            />
          </Link>
          {/* Discord, only shown on mobile */}
          <DiscordButton size="small" className="mmd:hidden" />
          <span className="ml-auto" />
          {showCreditPill && (
            <MyCredits
              variant="navigation"
              className={cx('flex-none', isMobile ? 'text-xs' : 'mlg:text-sm')}
            />
          )}

          {/* Generate button */}
          {isDesktop && (
            <div
              className={cx(
                'transition-[opacity,transform,width] duration-300 ease-in-out',
                showGenerateButton
                  ? 'opacity-100 translate-x-0 pointer-events-auto'
                  : 'opacity-0 -translate-x-4 pointer-events-none w-0 overflow-hidden -mr-4',
              )}
              {...(!showGenerateButton && { inert: true, 'aria-hidden': true })}
            >
              <GenerationButton />
            </div>
          )}
          {/* Search */}
          {isDesktop ? (
            <div
              className={cx(
                'transition-[opacity,transform,width] duration-300 ease-in-out',
                showingSearchBar
                  ? 'opacity-100 translate-x-0 pointer-events-auto'
                  : 'opacity-0 translate-x-4 pointer-events-none w-0 overflow-hidden -mr-4',
              )}
              {...(!showingSearchBar && { inert: true, 'aria-hidden': true })}
            >
              <SearchBar />
            </div>
          ) : (
            <IconButton
              key="mobile-search"
              component={Link}
              to="/search"
              className="size-8 bg-background-secondary border border-solid border-black/50 dark:border-white/50"
            >
              <MingcuteSearchLine className="flex-none size-5 text-primary" />
            </IconButton>
          )}

          <NotificationButton size={isMobile ? 'normal' : 'large'} />
          {/* {marketButton}
        {postWorkButton} */}
          <AccountButton
            size={isMobile ? 'normal' : 'large'}
            className="-mr-1"
          />
        </header>
      </div>
    </>
  )
})
AppHeader.displayName = 'AppHeader'

export default function AppLayout({ children }: PropsWithChildren) {
  const location = useLocation()
  const showingAppFooter = !useRouteHandleFlag('hideAppFooter')
  const showExpandedAppNav = useAtomValue(desktopNavigationExpandAtom)

  // const postWorkButton = <PostWorkButton />
  // const marketButton = <MarketButton />

  const isMobile = useIsMobile()

  // Use route.handle to determine if app nav should be shown
  const shouldShowAppNavFromRoute = useRouteHandleFlag('showAppNav')

  // Special case: on desktop, hide app nav for profile pages
  const isProfilePage = location.pathname.startsWith('/@')
  const showingAppNav =
    shouldShowAppNavFromRoute && !(isProfilePage && !isMobile)

  const isArtworkPage =
    location.pathname.startsWith('/artwork') ||
    location.pathname.startsWith('/video')
  const isInSearch = location.pathname.startsWith('/search')
  const isInMobileSearchResultPage = isMobile && isInSearch
  const showAppBanner = true

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--app-bottom-navigation-height',
      (isMobile ? APP_NAVIGATION_HEIGHT : 0) + 'px',
    )
  }, [isMobile])

  const containerProps: ContainerTypeMap['props'] = useMemo(() => {
    const config = {
      largeMaxWidth: {
        classes: {
          root: `max-w-[108rem] ${isMobile ? 'px-2 md:px-6' : 'px-0'}`,
        },
      },
      fullWidth: { maxWidth: false as const, classes: { root: '!px-0' } },
    }

    // NOTE: Special case for artwork page
    if (isArtworkPage)
      return {
        classes: {
          root: `max-w-[108rem] ${isMobile ? 'px-0 md:px-6' : 'px-0'}`,
        },
      }

    return (
      Object.entries({
        '/': config.largeMaxWidth,
        '/ranking': config.largeMaxWidth,
        '/search': config.largeMaxWidth,
        '/market': config.largeMaxWidth,
        '/market/': config.largeMaxWidth,
        '/generator/preset': config.largeMaxWidth,
        '/train-lora': config.fullWidth,
        '/user-rank': config.fullWidth,
        '/eap': config.fullWidth,
        '/membership/plans': {
          maxWidth: false as const,
          classes: { root: '!px-0' },
        },
        '/membership/credit-packs': {
          maxWidth: false as const,
          classes: { root: '!px-0' },
        },
        '/mio2/gem-pack': {
          maxWidth: false as const,
          classes: { root: '!px-0' },
        },
        '^/referral': {
          maxWidth: false as const,
          classes: { root: '!px-0' },
        },
        '/user-referral': {
          maxWidth: false as const,
          classes: { root: '!px-0' },
        },
        '^/world/': config.fullWidth,
        '^/event': config.fullWidth,
      }).find(([path]) => {
        return path.startsWith('^')
          ? location.pathname.startsWith(path.slice(1))
          : location.pathname === path
      })?.[1] ?? {}
    )
  }, [location, isMobile, isArtworkPage])

  if (isInMobileSearchResultPage)
    return <MobileSearchResultLayout>{children}</MobileSearchResultLayout>

  return (
    <>
      {/*<BackgroundBlobs />*/}

      <div className="flex flex-col min-h-screen">
        {/* Hide mobile app banner until mobile apps become stable */}
        {showAppBanner && showingAppNav && <OpenAppBanner />}
        <AppHeader />
        <div
          className={cx(
            'flex-1 transition-all duration-300',
            showExpandedAppNav
              ? styles['app-layout-expanded']
              : styles['app-layout'],
          )}
        >
          {showingAppNav && <AppNavigation />}
          <Container
            className={cx(
              'min-w-0 mmd:pl-3 mmd:pr-1',
              !showingAppNav && 'col-span-full',
            )}
            style={{ gridArea: showingAppNav ? 'main' : undefined }}
            {...containerProps}
          >
            {children}
          </Container>
        </div>
        {showingAppFooter && <AppFooter className="mt-auto" />}
      </div>
    </>
  )
}

function MobileSearchResultLayout(props: { children?: React.ReactNode }) {
  const { navbarRef, isNavbarVisible } = useHideableNavbar()

  const header = (
    <>
      <div style={{ height: 'var(--app-header-height)' }}></div>
      <nav
        ref={navbarRef}
        className={cx(
          'w-full bg-background z-20 fixed top-0 transition-transform',
        )}
        style={{
          boxShadow: '0 4px 12px #00000014, 0 0 1px #0100001a',
          transform: isNavbarVisible
            ? ''
            : 'translate3d(0, calc(-100% - 2px), 0)',
        }}
      >
        <MobileSearchBar className="px-4 py-2" />
      </nav>
    </>
  )

  return (
    <>
      {header}
      <div className="flex">
        <Container className={'lg:px-36 md:px-20'}>{props.children}</Container>
        <AppNavigation />
      </div>
    </>
  )
}

export function LayoutHeaderBanner() {
  const showingTopBanner = useAtomValue(headerBannerAtom)
  if (!showingTopBanner) return null

  /**
   * overflow-y-hidden to prevent vertical overflow due to countdown number flow
   * overflow-x-hidden to prevent horizontal overflow due to potential long text in some langs
   * Ensure those issues are handled before removing overflow-hidden
   */
  return <AppTopBannerSlot className="overflow-hidden" />
}

/**
 * Generate button in the site header. Should only be shown on desktop.
 */
function GenerationButton() {
  const { data: userInfo } = useMyInfo()

  const linkTo = useMemo(() => {
    return withQuery(
      '/generator/image',
      userInfo?.id
        ? {}
        : {
            showSignUpModal: 1,
          },
    )
  }, [userInfo?.id])

  return (
    <ButtonBase
      component={Link}
      className={cx(
        'px-4 py-2 font-medium no-underline bg-theme-primary hover:bg-violet-500 dark:bg-violet-600 text-white rounded-full transition active:bg-violet-600 dark:active:bg-violet-700 border-none h-10 gap-0.5',
      )}
      onClick={() => {
        startEventGroup('generate_source')
        track('new_btn_click')
      }}
      to={linkTo}
    >
      <IcBaselineAdd className="-ml-[6px] size-6" />
      <span className="leading-none">
        <Text i18nKey="common:generate.label" />
      </span>
    </ButtonBase>
  )
}
